import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, Divider } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { resolveVerification, verificationValidation } from '@/api/auth-flow';

interface Props {
  email: string;
  /** Account verification was incomplete (vs. email MFA on login). */
  uncompleted: boolean;
  onBack: () => void;
}

interface FormValues {
  verification: string;
}

const RESEND_SECONDS = 60;

export function EmailVerificationForm({ email, uncompleted, onBack }: Props) {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { verification: '' } });

  const validate = useMutation({ mutationFn: verificationValidation });
  const resend = useMutation({ mutationFn: resolveVerification });

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const onSubmit = async (values: FormValues) => {
    setError(null);
    try {
      const res = await validate.mutateAsync({ email, verification: values.verification });
      if (res.errors?.length) {
        setError(res.errors[0].message);
        return;
      }
      if (setSession(res.data ?? {})) {
        navigate('/', { replace: true });
      } else {
        setError('Invalid response from server. Please try again.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed.');
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(null);
    const res = await resend.mutateAsync(email);
    if (res.errors?.length) {
      setError(res.errors[0].message);
      return;
    }
    if (res.data?.created) {
      setCooldown(RESEND_SECONDS);
      toast.success("We've sent a new verification code to your email.");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {uncompleted ? (
        <Alert tone="warning">
          Your account verification is incomplete. Enter the code we sent to
          complete it.
        </Alert>
      ) : null}
      {error ? <Alert tone="danger" title={error} /> : null}

      <Field
        label="Verification code"
        required
        description={`Enter the code sent to ${email}.`}
        error={errors.verification?.message}
      >
        {(p) => (
          <Input
            {...p}
            inputMode="numeric"
            placeholder="0000000"
            autoComplete="one-time-code"
            {...register('verification', {
              required: 'Code is required.',
              minLength: { value: 6, message: 'Code must be 6–20 characters.' },
              maxLength: { value: 20, message: 'Code must be 6–20 characters.' },
            })}
          />
        )}
      </Field>

      <div style={{ textAlign: 'right', fontSize: 'var(--vn-font-size-md)' }}>
        {cooldown > 0 ? (
          <span style={{ color: 'var(--vn-fg-muted)' }}>Resend in {cooldown}s</span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={resend.isPending}
            style={{
              background: 'none',
              border: 0,
              cursor: 'pointer',
              color: 'var(--vn-primary-solid)',
              textDecoration: 'underline',
            }}
          >
            Resend code
          </button>
        )}
      </div>

      <Button type="submit" variant="primary" fullWidth loading={validate.isPending}>
        Verify
      </Button>

      <Divider>or</Divider>

      <Button type="button" variant="secondary" fullWidth onClick={onBack}>
        Use another account
      </Button>
    </form>
  );
}
